import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, HeartHandshake, X } from "lucide-react";
import {
  NEPAL_RELIEF,
  NEPAL_RELIEF_DISMISS_KEY,
  NEPAL_RELIEF_EVENT,
} from "../../data/nepalRelief";

const NepalReliefPopup = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!NEPAL_RELIEF.active) return undefined;
    if (sessionStorage.getItem(NEPAL_RELIEF_DISMISS_KEY)) return undefined;
    const timer = window.setTimeout(() => setVisible(true), 900);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    const closeOnEscape = (event) => event.key === "Escape" && close();
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [visible]);

  const close = () => {
    setVisible(false);
    sessionStorage.setItem(NEPAL_RELIEF_DISMISS_KEY, "true");
    window.dispatchEvent(new Event(NEPAL_RELIEF_EVENT));
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="nepal-relief-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          onClick={close}
        >
          <motion.section
            className="nepal-relief"
            role="dialog"
            aria-modal="true"
            aria-labelledby="nepal-relief-title"
            initial={{ opacity: 0, y: 34, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              className="nepal-relief-close"
              onClick={close}
              aria-label="Close Together for Nepal announcement"
            >
              <X size={17} />
            </button>

            <a
              className="nepal-relief-flyer"
              href={NEPAL_RELIEF.flyer}
              target="_blank"
              rel="noreferrer"
            >
              <img src={NEPAL_RELIEF.flyer} alt={NEPAL_RELIEF.flyerAlt} />
              <span>View full flyer <ArrowUpRight size={13} /></span>
            </a>

            <div className="nepal-relief-body">
              <h2 id="nepal-relief-title">
                {NEPAL_RELIEF.title} <em>{NEPAL_RELIEF.subtitle}</em>
              </h2>
              <p className="nepal-relief-intro">{NEPAL_RELIEF.intro}</p>

              <p className="nepal-relief-label">Our commitment</p>
              <ul className="nepal-relief-list">
                {NEPAL_RELIEF.commitments.map((item) => (
                  <li key={item.id}>
                    <div>
                      <strong>{item.title}</strong>
                      <small>{item.meta}</small>
                      <p>{item.detail}</p>
                    </div>
                    <Link to={item.to} onClick={close}>
                      {item.ctaLabel} <ArrowUpRight size={14} />
                    </Link>
                  </li>
                ))}
              </ul>

              <p className="nepal-relief-contribution">{NEPAL_RELIEF.contribution}</p>

              <footer className="nepal-relief-foot">
                <p className="nepal-relief-closing">{NEPAL_RELIEF.closing}</p>
                <button className="nepal-relief-later" onClick={close}>
                  Close
                </button>
              </footer>
            </div>
          </motion.section>

          <style>{`
            .nepal-relief-backdrop {
              position: fixed;
              inset: 0;
              z-index: 10001;
              display: grid;
              place-items: center;
              padding: 1rem;
              background: rgba(6,17,10,.82);
              backdrop-filter: blur(12px);
            }
            .nepal-relief {
              position: relative;
              display: grid;
              grid-template-columns: minmax(0, 300px) minmax(0, 1fr);
              gap: clamp(1.25rem, 2.5vw, 2.25rem);
              width: min(1020px, 100%);
              max-height: calc(100dvh - 2rem);
              overflow-y: auto;
              padding: clamp(1.5rem, 3vw, 2.5rem);
              border: 1px solid rgba(126,193,131,.28);
              border-radius: 28px;
              background:
                radial-gradient(circle at 88% 4%, rgba(30,122,62,.28), transparent 45%),
                #0b0908;
              color: #f8f2ea;
              box-shadow: 0 45px 120px rgba(0,0,0,.55), inset 0 1px 1px rgba(255,255,255,.08);
            }
            .nepal-relief-close {
              position: absolute;
              z-index: 3;
              top: 1rem;
              right: 1rem;
              display: grid;
              place-items: center;
              width: 38px;
              height: 38px;
              border: 1px solid rgba(248,242,234,.18);
              border-radius: 50%;
              background: rgba(11,9,8,.72);
              color: #f8f2ea;
              cursor: pointer;
              transition: transform .45s cubic-bezier(.22,1,.36,1), background .45s cubic-bezier(.22,1,.36,1);
            }
            .nepal-relief-close:hover { transform: rotate(8deg); background: #1e7a3e; }

            .nepal-relief-flyer {
              position: relative;
              display: block;
              overflow: hidden;
              align-self: start;
              border: 1px solid rgba(248,242,234,.12);
              border-radius: 20px;
              background: #17100e;
              text-decoration: none;
            }
            .nepal-relief-flyer img { display: block; width: 100%; height: auto; }
            .nepal-relief-flyer span {
              position: absolute;
              right: .6rem;
              bottom: .6rem;
              display: inline-flex;
              align-items: center;
              gap: .3rem;
              padding: .38rem .7rem;
              border-radius: 999px;
              background: rgba(11,9,8,.82);
              color: #f8f2ea;
              font-size: .66rem;
              font-weight: 800;
              letter-spacing: .04em;
            }
            .nepal-relief-flyer:hover span { background: #1e7a3e; }

            .nepal-relief-body { min-width: 0; padding-right: 2.4rem; }
            .nepal-relief h2 {
              margin: .75rem 0 .7rem;
              color: #f8f2ea;
              font-family: var(--font-display);
              font-size: clamp(1.9rem, 3.2vw, 2.9rem);
              font-weight: 900;
              line-height: 1;
              letter-spacing: -.045em;
            }
            .nepal-relief h2 em {
              display: block;
              margin-top: .3rem;
              color: #7ec183;
              font-family: var(--font-serif);
              font-size: .52em;
              font-weight: 480;
              letter-spacing: -.01em;
            }
            .nepal-relief-intro {
              margin: 0 0 1.35rem;
              color: rgba(248,242,234,.7);
              font-size: .88rem;
              line-height: 1.65;
            }
            .nepal-relief-label {
              margin: 0 0 .6rem;
              color: rgba(248,242,234,.5);
              font-size: .62rem;
              font-weight: 800;
              letter-spacing: .2em;
              text-transform: uppercase;
            }
            .nepal-relief-list { display: grid; gap: .6rem; margin: 0 0 1.1rem; padding: 0; list-style: none; }
            .nepal-relief-list li {
              display: flex;
              flex-wrap: wrap;
              align-items: center;
              justify-content: space-between;
              gap: .8rem;
              padding: .85rem 1rem;
              border: 1px solid rgba(248,242,234,.13);
              border-radius: 16px;
              background: rgba(248,242,234,.045);
            }
            .nepal-relief-list li > div { min-width: 0; flex: 1 1 260px; }
            .nepal-relief-list strong {
              display: block;
              font-family: var(--font-display);
              font-size: 1rem;
              font-weight: 800;
              letter-spacing: -.02em;
            }
            .nepal-relief-list small {
              display: block;
              margin-top: .12rem;
              color: #7ec183;
              font-size: .62rem;
              font-weight: 800;
              letter-spacing: .12em;
              text-transform: uppercase;
            }
            .nepal-relief-list p {
              margin: .4rem 0 0;
              color: rgba(248,242,234,.66);
              font-size: .78rem;
              line-height: 1.5;
            }
            .nepal-relief-list a {
              display: inline-flex;
              align-items: center;
              min-height: 44px;
              gap: .4rem;
              flex: 0 0 auto;
              padding: .5rem .95rem;
              border-radius: 999px;
              background: #1e7a3e;
              color: #f4fff6;
              font-size: .72rem;
              font-weight: 800;
              text-decoration: none;
              transition: background .35s cubic-bezier(.22,1,.36,1), transform .35s cubic-bezier(.22,1,.36,1);
            }
            .nepal-relief-list a:hover { background: #145c2e; transform: translateY(-2px); }

            .nepal-relief-contribution {
              margin: 0;
              padding: .8rem 1rem;
              border-radius: 14px;
              background: rgba(226,194,136,.14);
              border: 1px solid rgba(226,194,136,.28);
              color: #f0dcb4;
              font-size: .8rem;
              font-weight: 700;
              line-height: 1.55;
            }
            .nepal-relief-foot {
              display: flex;
              flex-wrap: wrap;
              align-items: center;
              justify-content: space-between;
              gap: .75rem;
              margin-top: 1.15rem;
              padding-top: 1rem;
              border-top: 1px solid rgba(248,242,234,.12);
            }
            .nepal-relief-closing {
              margin: 0;
              color: #7ec183;
              font-family: var(--font-serif);
              font-size: .92rem;
              font-weight: 480;
            }
            .nepal-relief-later {
              border: 0;
              background: none;
              color: rgba(248,242,234,.48);
              font: inherit;
              font-size: .72rem;
              cursor: pointer;
            }
            .nepal-relief-later:hover { color: #f8f2ea; }

            @media (max-width: 860px) {
              .nepal-relief { grid-template-columns: 1fr; }
              .nepal-relief-flyer { max-width: 320px; margin: 0 auto; }
              .nepal-relief-body { padding-right: 0; }
            }
            @media (max-width: 720px) {
              .nepal-relief-backdrop { align-items: end; padding: .75rem; }
              .nepal-relief { border-radius: 24px; padding: 1.5rem 1.15rem 1.35rem; max-height: calc(100dvh - 1.5rem); }
              .nepal-relief h2 { font-size: clamp(1.7rem, 7.5vw, 2.2rem); }
              .nepal-relief-list li { padding: .8rem .85rem; }
              .nepal-relief-list a { width: 100%; justify-content: center; }
              .nepal-relief-foot { justify-content: center; text-align: center; }
            }
            @media (prefers-reduced-motion: reduce) {
              .nepal-relief, .nepal-relief-backdrop { transition-duration: .01ms !important; }
            }
          `}</style>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NepalReliefPopup;
